import {
  DEFAULT_JOURNEY_CANVAS_HEIGHT,
  DEFAULT_JOURNEY_CANVAS_WIDTH,
  JOURNEY_BOARD_PADDING_X,
  JOURNEY_BOARD_PADDING_Y,
} from './constants';
import {
  JourneyMapRenderableItem,
  JourneyMapRenderableLink,
  JourneyMapSectionDelimiterLike,
} from '../../components/Common/JourneyMap/journeyMapBoardTypes';
import {
  JOURNEY_NODE_HEIGHT,
  JOURNEY_NODE_WIDTH,
} from './constants';

export const toNumber = (value: any, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const extractId = (value: any): string | null => {
  if (value == null) return null;
  if (typeof value === 'object') return value.id != null ? String(value.id) : null;
  return String(value);
};

export const resolveSourceId = (link: JourneyMapRenderableLink): string | null =>
  extractId(link?.from_item) ||
  extractId(link?.source_item) ||
  extractId(link?.source) ||
  extractId(link?.from) ||
  extractId(link?.from_journey_map_item) ||
  extractId(link?.source_journey_map_item) ||
  null;

export const resolveTargetId = (link: JourneyMapRenderableLink): string | null =>
  extractId(link?.to_item) ||
  extractId(link?.target_item) ||
  extractId(link?.target) ||
  extractId(link?.to) ||
  extractId(link?.to_journey_map_item) ||
  extractId(link?.target_journey_map_item) ||
  null;

export const resolveLinkType = (link: JourneyMapRenderableLink): string =>
  String(link?.link_type ?? link?.type ?? 'default');

export const parseJourneyMetadata = (metadata: any) => {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch {
      return {};
    }
  }
  return typeof metadata === 'object' ? metadata : {};
};

export const getItemPosition = (item: JourneyMapRenderableItem) => ({
  x: toNumber(item?.position_x ?? item?.metadata?.position_x, 0),
  y: toNumber(item?.position_y ?? item?.metadata?.position_y, 0),
});

export const normalizeCanvasSize = (canvasWidth?: number, canvasHeight?: number) => ({
  width: Math.max(1, toNumber(canvasWidth, DEFAULT_JOURNEY_CANVAS_WIDTH)),
  height: Math.max(1, toNumber(canvasHeight, DEFAULT_JOURNEY_CANVAS_HEIGHT)),
});

export const getItemLayoutExtents = (items: JourneyMapRenderableItem[]) => {
  if (!items.length) {
    return {
      minX: 0,
      minY: 0,
      maxX: DEFAULT_JOURNEY_CANVAS_WIDTH,
      maxY: DEFAULT_JOURNEY_CANVAS_HEIGHT,
    };
  }

  const rawPositions = items.map((item) => getItemPosition(item));

  const minX = Math.min(...rawPositions.map((p) => p.x));
  const minY = Math.min(...rawPositions.map((p) => p.y));
  const maxX = Math.max(...rawPositions.map((p) => p.x + JOURNEY_NODE_WIDTH));
  const maxY = Math.max(...rawPositions.map((p) => p.y + JOURNEY_NODE_HEIGHT));

  return { minX, minY, maxX, maxY };
};

export const normalizeItems = (items: JourneyMapRenderableItem[]) => {
  const extents = getItemLayoutExtents(items);

  const offsetX =
    JOURNEY_BOARD_PADDING_X + (extents.minX < 0 ? Math.abs(extents.minX) : 0);
  const offsetY =
    JOURNEY_BOARD_PADDING_Y + (extents.minY < 0 ? Math.abs(extents.minY) : 0);

  return items.map((item) => {
    const pos = getItemPosition(item);
    const metadata = (item?.metadata && typeof item.metadata === 'object') ? item.metadata : {};
    const progress =
      item?.progress && typeof item.progress === 'object'
        ? item.progress
        : null;

    const resolvedReferencedTitle = String(
      (item as any)?.resolvedReferencedTitle ||
      metadata?.resolvedReferencedTitle ||
      metadata?.dcloud_selected_item?.name ||
      ''
    ).trim();

    const taskSubmission =
      item?.task_submission ||
      progress?.task_submission ||
      null;

    const pathState =
      String(item?.path_state || progress?.path_state || '').trim() || null;

    const syncStatus =
      String(item?.sync_status || progress?.sync_status || '').trim() || null;

    const statusName =
      String(item?.status_name || progress?.status_name || '').trim() || null;

    const isPendingValidation =
      item?.is_pending_validation === true ||
      progress?.is_pending_validation === true ||
      pathState === 'pending_validation' ||
      syncStatus === 'pending';

    return {
      ...item,
      resolvedReferencedTitle,
      progress,
      task_submission: taskSubmission,
      path_state: pathState,
      sync_status: syncStatus,
      status_name: statusName,
      is_pending_validation: isPendingValidation,
      _rawX: pos.x,
      _rawY: pos.y,
      _x: offsetX + pos.x,
      _y: offsetY + pos.y,
    };
  });
};

export const normalizeDelimiters = (sectionDelimiters: JourneyMapSectionDelimiterLike[] = []) => {
  const minX = Math.min(
    0,
    ...sectionDelimiters.map((delimiter) => toNumber(delimiter?.x, 0)),
  );
  const minY = Math.min(
    0,
    ...sectionDelimiters.map((delimiter) => toNumber(delimiter?.y, 0)),
  );

  const offsetX = JOURNEY_BOARD_PADDING_X + (minX < 0 ? Math.abs(minX) : 0);
  const offsetY = JOURNEY_BOARD_PADDING_Y + (minY < 0 ? Math.abs(minY) : 0);

  return sectionDelimiters
    .filter((delimiter) => !delimiter?.isDeleted)
    .map(
      (delimiter, idx): JourneyMapSectionDelimiterLike & {
        _key: string;
        _x: number;
        _y: number;
        orientation: 'horizontal' | 'vertical';
        style: string;
        color: string;
        label: string;
        width: number;
      } => ({
        ...delimiter,
        _key: String(delimiter?.id ?? delimiter?.tempId ?? `delimiter-${idx}`),
        _x: offsetX + toNumber(delimiter?.x, 0),
        _y: offsetY + toNumber(delimiter?.y, 0),
        orientation: delimiter?.orientation === 'vertical' ? 'vertical' : 'horizontal',
        style: delimiter?.style || delimiter?.lineStyle || 'solid',
        color: delimiter?.color || 'neutral',
        label: delimiter?.label || '',
        width: toNumber(delimiter?.width, 4),
      }),
    );
};

export const getBoardBounds = (
  width: number,
  height: number,
  items: any[] = [],
  delimiters: any[] = [],
) => {
  const itemRight = items.length
    ? Math.max(...items.map((item) => toNumber(item?._x, 0) + JOURNEY_NODE_WIDTH))
    : width;

  const itemBottom = items.length
    ? Math.max(...items.map((item) => toNumber(item?._y, 0) + JOURNEY_NODE_HEIGHT))
    : height;

  const delimiterRight = delimiters.length
    ? Math.max(...delimiters.map((delimiter) => toNumber(delimiter?._x, 0)))
    : 0;

  const delimiterBottom = delimiters.length
    ? Math.max(...delimiters.map((delimiter) => toNumber(delimiter?._y, 0)))
    : 0;

  return {
    width: Math.max(width + JOURNEY_BOARD_PADDING_X * 2, itemRight, delimiterRight) + JOURNEY_BOARD_PADDING_X,
    height: Math.max(height + JOURNEY_BOARD_PADDING_Y * 2, itemBottom, delimiterBottom) + JOURNEY_BOARD_PADDING_Y,
  };
};

export const getDelimiterColor = (color?: string) => {
  switch (String(color || '').toLowerCase()) {
    case 'primary':
    case 'info':
    case 'blue':
      return 'var(--hbr-color-info)';
    case 'success':
    case 'green':
      return 'var(--hbr-color-success)';
    case 'warning':
    case 'yellow':
      return 'var(--hbr-color-warning)';
    case 'danger':
    case 'negative':
    case 'red':
      return 'var(--hbr-color-danger)';
    case 'accent':
    case 'interact':
      return 'var(--hbr-color-interact)';
    case 'neutral':
    case 'gray':
    case 'grey':
    default:
      return 'var(--hbr-color-neutral-border)';
  }
};

export const getDelimiterBorderStyle = (style?: string) => {
  switch (String(style || '').toLowerCase()) {
    case 'dashed':
      return 'dashed';
    case 'dotted':
      return 'dotted';
    case 'solid':
    default:
      return 'solid';
  }
};

export const isJourneyTaskItem = (item?: JourneyMapRenderableItem | null) =>
  String(item?.item_type || '').toLowerCase() === 'task';

export const isShadowingTaskItem = (item?: JourneyMapRenderableItem | null) => {
  const metadata = parseJourneyMetadata(item?.metadata);
  return (
    isJourneyTaskItem(item) &&
    String(metadata?.task_type || '').trim().toLowerCase() === 'shadowing'
  );
};

export const getJourneyItemStatusLabel = ({
                                            completed,
                                            pendingValidation,
                                            overdue,
                                            isRequired,
                                            notApplicable,
                                            locked,
                                          }: {
  completed?: boolean;
  pendingValidation?: boolean;
  overdue?: boolean;
  isRequired?: boolean;
  notApplicable?: boolean;
  locked?: boolean;
}) => {
  if (completed) return 'Completed';
  if (pendingValidation) return 'Pending Validation';
  if (notApplicable) return 'Not Applicable';
  if (locked) return 'Locked';
  if (overdue) return 'Overdue';
  if (isRequired === false) return 'Optional';
  return 'Available';
};

export const getJourneyVisualState = ({
                                        completed,
                                        pendingValidation,
                                        overdue,
                                        isRequired,
                                        notApplicable,
                                        locked,
                                      }: {
  completed?: boolean;
  pendingValidation?: boolean;
  overdue?: boolean;
  isRequired?: boolean;
  notApplicable?: boolean;
  locked?: boolean;
}) => {
  if (completed) {
    return {
      sentiment: 'success' as const,
      label: 'Completed',
      backgroundColor: 'var(--hbr-color-success-bg)',
      borderColor: 'var(--hbr-color-success-border)',
      textColor: 'var(--hbr-color-success-text)',
    };
  }

  if (pendingValidation) {
    return {
      sentiment: 'warning' as const,
      label: 'Pending Validation',
      backgroundColor: 'var(--hbr-color-warning-bg)',
      borderColor: 'var(--hbr-color-warning-border)',
      textColor: 'var(--hbr-color-warning-text)',
    };
  }

  if (notApplicable) {
    return {
      sentiment: undefined,
      label: 'Not Applicable',
      backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
      borderColor: 'var(--hbr-color-neutral-border)',
      textColor: 'var(--hbr-color-neutral-text)',
    };
  }

  if (locked) {
    return {
      sentiment: undefined,
      label: 'Locked',
      backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
      borderColor: 'var(--hbr-color-neutral-border)',
      textColor: 'var(--hbr-color-neutral-text)',
    };
  }

  if (overdue) {
    return {
      sentiment: 'danger' as const,
      label: 'Overdue',
      backgroundColor: 'var(--hbr-color-danger-bg)',
      borderColor: 'var(--hbr-color-danger-border)',
      textColor: 'var(--hbr-color-danger-text)',
    };
  }

  if (isRequired === false) {
    return {
      sentiment: undefined,
      label: 'Optional',
      backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
      borderColor: 'var(--hbr-color-neutral-border)',
      textColor: 'var(--hbr-color-neutral-text)',
    };
  }

  return {
    sentiment: 'info' as const,
    label: 'Available',
    backgroundColor: 'var(--hbr-color-info-bg)',
    borderColor: 'var(--hbr-color-info-border)',
    textColor: 'var(--hbr-color-info-text)',
  };
};

export const getJourneyCardTitle = (item: JourneyMapRenderableItem) =>
  item?.name || 'Untitled Item';

export const getJourneyCardSubtitle = (item: JourneyMapRenderableItem) =>
  // The admin-set display label wins over the raw file name, so a friendly label shows on the card.
  (item as any)?.metadata?.label ||
  (item as any)?.resolvedReferencedTitle ||
  (item as any)?.metadata?.resolvedReferencedTitle ||
  (item as any)?.metadata?.dcloud_selected_item?.name ||
  item?.description ||
  ' ';

// Short, human-readable labels for a file-backed item's type, shown as a tag on the card. The
// server-derived `render_kind` is the primary signal; when it is `download`/empty (e.g. Word,
// which has no in-app renderer's own kind) we fall back to the file extension so the tag is still
// meaningful.
const RENDER_KIND_TAGS: Record<string, string> = {
  pdf: 'PDF',
  markdown: 'Markdown',
  spreadsheet: 'Excel',
  presentation: 'PowerPoint',
  image: 'Image',
  text: 'Text',
  video: 'Video',
};

const EXTENSION_TAGS: Record<string, string> = {
  docx: 'Word',
  doc: 'Word',
  xlsx: 'Excel',
  xls: 'Excel',
  csv: 'CSV',
  pptx: 'PowerPoint',
  ppt: 'PowerPoint',
  pdf: 'PDF',
  md: 'Markdown',
  txt: 'Text',
  zip: 'ZIP',
};

/**
 * The file-type tag for a file-backed journey-map item, or '' when the item is not file-backed.
 * Reads the cached `provider_config.file` written at attach time.
 */
export const getJourneyFileTypeLabel = (item: JourneyMapRenderableItem): string => {
  const file = (item as any)?.provider_config?.file;
  if (!file) return '';

  const kind = String(file.render_kind || '').toLowerCase();
  if (kind && kind !== 'download' && RENDER_KIND_TAGS[kind]) return RENDER_KIND_TAGS[kind];

  const name = String(file.file_name || '');
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  if (ext && EXTENSION_TAGS[ext]) return EXTENSION_TAGS[ext];
  if (ext) return ext.toUpperCase();

  // A file is attached but we cannot name its type — still denote it as a file.
  return 'File';
};

/**
 * Provider is the axis that actually differentiates journey-map cards (File, Video, Moodle,
 * Assessment, …), so the card shows it as a single color-coded chip in a fixed location.
 * `accent` colors are a categorical set — deliberately non-semantic so no provider reads as
 * "good"/"error" — with a neutral fallback for unknown providers.
 */
const PROVIDER_TAG_META: Record<string, { label: string; accent: string }> = {
  file: { label: 'File', accent: '#8FB2E0' },
  video: { label: 'Video', accent: '#CBA6E2' },
  markdown: { label: 'Guide', accent: '#9BC7B4' },
  link: { label: 'Link', accent: '#AEB6E8' },
  moodle: { label: 'Moodle', accent: '#E6BE8C' },
  dcloud: { label: 'dCloud', accent: '#9FD3DF' },
  cyber_academy: { label: 'Cyber Academy', accent: '#DDA9C8' },
  mindtickle: { label: 'Mindtickle', accent: '#BCB2E6' },
  journey_map: { label: 'Journey Map', accent: '#B7CE98' },
  assessment: { label: 'Assessment', accent: '#E3A9A2' },
};

const PROVIDER_TAG_FALLBACK_ACCENT = '#C4C9CE';

/**
 * The color-coded provider chip for a journey-map card, or null when the item has no provider
 * (and isn't a legacy assessment item). Assessment is treated as a provider per the data-model
 * direction, so a legacy `item_type === 'assessment'` still resolves to the Assessment chip.
 */
export const getJourneyProviderTag = (
  item: JourneyMapRenderableItem,
): { label: string; accent: string } | null => {
  const raw = String(item?.provider || '').trim().toLowerCase().replace(/\s+/g, '_');
  const key =
    raw ||
    (String(item?.item_type || '').toLowerCase() === 'assessment' ? 'assessment' : '');
  if (!key) return null;

  const meta = PROVIDER_TAG_META[key];
  if (meta) return meta;

  // Unknown provider: still show it, title-cased, with the neutral accent.
  const label = key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label, accent: PROVIDER_TAG_FALLBACK_ACCENT };
};

export const isJourneyItemOverdue = (
  item: JourneyMapRenderableItem,
  completed: boolean,
): boolean => {
  if (completed) return false;
  const due = item?.available_to || item?.due_date || null;
  if (!due) return false;
  const ts = Date.parse(String(due));
  return !Number.isNaN(ts) && ts < Date.now();
};

export const buildItemMapById = (items: any[]) => {
  const map: Record<string, any> = {};
  items.forEach((item) => {
    if (item?.id != null) {
      map[String(item.id)] = item;
    }
  });
  return map;
};

export const resolveRenderableLinks = (
  links: JourneyMapRenderableLink[],
  itemById: Record<string, any>,
) => {
  return links
    .map((link) => ({
      raw: link,
      sourceId: resolveSourceId(link),
      targetId: resolveTargetId(link),
      type: resolveLinkType(link),
    }))
    .filter(
      (link) => {
        return !!link.sourceId &&
          !!link.targetId &&
          !!itemById[link.sourceId!] &&
          !!itemById[link.targetId!];
      },
    );
};

export const getEdgeAppearance = (linkType?: string) => {
  const normalizedType = String(linkType || 'default').toLowerCase();

  if (normalizedType === 'conditional') {
    return {
      stroke: 'var(--hbr-color-interact)',
      className: 'journey-map-board__edge journey-map-board__edge--conditional',
    };
  }

  if (normalizedType === 'test_out') {
    return {
      stroke: 'var(--hbr-color-warning)',
      className: 'journey-map-board__edge journey-map-board__edge--testout',
    };
  }

  if (normalizedType === 'alternate') {
    return {
      stroke: 'var(--hbr-color-info)',
      className: 'journey-map-board__edge journey-map-board__edge--alternate',
    };
  }

  if (normalizedType === 'pass') {
    return {
      stroke: 'var(--hbr-color-success)',
      className: 'journey-map-board__edge',
    };
  }

  if (normalizedType === 'fail' || normalizedType === 'fallback') {
    return {
      stroke: 'var(--hbr-color-danger)',
      className: 'journey-map-board__edge',
    };
  }

  if (normalizedType === 'skip' || normalizedType === 'optional') {
    return {
      stroke: 'var(--hbr-color-neutral-border)',
      className: 'journey-map-board__edge journey-map-board__edge--conditional',
    };
  }

  return {
    stroke: 'var(--hbr-color-neutral-border)',
    className: 'journey-map-board__edge',
  };
};
